// color-exempt-file — getThemeClasses() est une 3e palette de marque par
// produit (blue/violet/emerald/amber), même statut que doo-* et
// dashboard/utils.ts::getThemeColors : littérale par design, paire
// light/dark sur les clés texte lisible (text/quotaText/headerText/
// conversationIcon) depuis Phase 6 ui-themes ; les tints/gradients
// décoratifs restent inchangés (déjà lisibles sur les deux fonds).
import { Calendar, FileText, Clock, Brain, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type ProductType = "date" | "form" | "availability" | "quizz";
export type ThemeColor = "blue" | "violet" | "emerald" | "amber";

export interface ProductConfig {
  type: ProductType;
  theme: ThemeColor;
  icon: LucideIcon;
  iconName: string; // Pour lazy loading dans les layouts
  name: string;
  namePlural: string;
  layoutName: string; // "DooDates1", "DooDates2", "DooDates3", "Quizz"
  baseRoute: string;
  createRoute: string;
  workspaceRoute: string;
  dashboardRoute: string;
  listRoute: string;
  settingsRoute: string;
  pricingRoute: string;
  docsRoute: string;
  journalRoute: string;
  createLabel: string;
  emptyStateTitle: string;
  emptyStateDescription: string;
  emptyStateButtonLabel: string;
  dashboardTitle: string;
  dashboardDescription: string;
  listLabel: string; // "Mes Sondages", "Mes Formulaires", "Mes Disponibilités", "Mes Quiz"
  headerDisplayName: string; // "Date Polls", "Form Polls", etc.
  headerGradient: string; // classes Tailwind pour bg-gradient-to-r ; suit toujours --primary (voir ui-themes)
  extraLinks?: { label: string; route: string; icon: LucideIcon }[];
}

export const PRODUCT_CONFIGS: Record<ProductType, ProductConfig> = {
  date: {
    type: "date",
    theme: "blue",
    icon: Calendar,
    iconName: "Calendar",
    name: "Sondage",
    namePlural: "Sondages",
    layoutName: "DooDates1",
    baseRoute: "/",
    createRoute: "/create",
    workspaceRoute: "/create",
    dashboardRoute: "/tableau-de-bord",
    listRoute: "/tableau-de-bord",
    settingsRoute: "/data-control",
    pricingRoute: "/pricing",
    docsRoute: "/docs",
    journalRoute: "/journal",
    createLabel: "Trouver un créneau",
    emptyStateTitle: "title.date",
    emptyStateDescription: "description.date",
    emptyStateButtonLabel: "button.date",
    dashboardTitle: "dashboard.title.date",
    dashboardDescription: "dashboard.description.date",
    listLabel: "Mes Sondages",
    headerDisplayName: "Date Polls",
    headerGradient: "from-primary to-primary/90",
    extraLinks: [{ label: "Roadmap", route: "/roadmap", icon: Users }],
  },
  form: {
    type: "form",
    theme: "violet",
    icon: FileText,
    iconName: "ClipboardList", // Pour le layout creator
    name: "Formulaire",
    namePlural: "Formulaires",
    layoutName: "DooDates2",
    baseRoute: "/",
    createRoute: "/create",
    workspaceRoute: "/create",
    dashboardRoute: "/tableau-de-bord",
    listRoute: "/tableau-de-bord",
    settingsRoute: "/data-control",
    pricingRoute: "/pricing",
    docsRoute: "/docs",
    journalRoute: "/journal",
    createLabel: "Nouveau Formulaire",
    emptyStateTitle: "title.form",
    emptyStateDescription: "description.form",
    emptyStateButtonLabel: "button.form",
    dashboardTitle: "dashboard.title.form",
    dashboardDescription: "dashboard.description.form",
    listLabel: "Mes Formulaires",
    headerDisplayName: "Form Polls",
    headerGradient: "from-primary to-primary/90",
    extraLinks: [{ label: "Roadmap", route: "/roadmap", icon: Users }],
  },
  availability: {
    type: "availability",
    theme: "emerald",
    icon: Clock,
    iconName: "Clock",
    name: "Disponibilité",
    namePlural: "Disponibilités",
    layoutName: "DooDates3",
    baseRoute: "/",
    createRoute: "/create",
    workspaceRoute: "/create",
    dashboardRoute: "/tableau-de-bord",
    listRoute: "/tableau-de-bord",
    settingsRoute: "/data-control",
    pricingRoute: "/pricing",
    docsRoute: "/docs",
    journalRoute: "/journal",
    createLabel: "Nouvelle Disponibilité",
    emptyStateTitle: "title.availability",
    emptyStateDescription: "description.availability",
    emptyStateButtonLabel: "button.availability",
    dashboardTitle: "dashboard.title.availability",
    dashboardDescription: "dashboard.description.availability",
    listLabel: "Mes Disponibilités",
    headerDisplayName: "Availability",
    headerGradient: "from-primary to-primary/90",
  },
  quizz: {
    type: "quizz",
    theme: "amber",
    icon: Brain,
    iconName: "Brain",
    name: "Quiz",
    namePlural: "Quiz",
    layoutName: "Quizz",
    baseRoute: "/",
    createRoute: "/create",
    workspaceRoute: "/create",
    dashboardRoute: "/tableau-de-bord",
    listRoute: "/tableau-de-bord",
    settingsRoute: "/data-control",
    pricingRoute: "/pricing",
    docsRoute: "/docs",
    journalRoute: "/journal",
    createLabel: "Nouveau Quiz",
    emptyStateTitle: "title.quizz",
    emptyStateDescription: "description.quizz",
    emptyStateButtonLabel: "button.quizz",
    dashboardTitle: "dashboard.title.quizz",
    dashboardDescription: "dashboard.description.quizz",
    listLabel: "Mes Quiz",
    headerDisplayName: "Quizz",
    headerGradient: "from-primary to-primary/90",
    extraLinks: [
      { label: "Historique enfants", route: "/quizz/history", icon: Users },
    ],
  },
};

export function getProductConfig(type: ProductType): ProductConfig {
  return PRODUCT_CONFIGS[type];
}

// Chrome interactif (sidebar active, CTA, quota, header) — suit --primary du
// thème actif, plus le violet/emerald/amber/bleu par produit d'avant Phase 6.
// `theme` (ThemeColor : blue/violet/emerald/amber, un par produit) ne fait
// plus varier ces classes : dans une app donnée il vaut toujours la même
// valeur (date→blue, form→violet, ...), donc ça ne distinguait jamais rien
// à l'usage — seulement le sélecteur de style (--primary) doit piloter cet
// accent désormais (bug #5 : l'état actif de la sidebar restait bleu sous
// Corail alors que le survol, déjà sur bg-accent, suivait déjà le thème).
const PRIMARY_THEME_CLASSES = {
  bg: "bg-primary/20",
  text: "text-primary",
  border: "border-primary/30",
  hover: "hover:bg-primary/20",
  gradientFrom: "from-primary/30",
  gradientTo: "to-primary/20",
  borderGradient: "border-primary/30",
  button: "bg-primary hover:bg-primary/90",
  buttonGradient:
    "from-primary to-primary hover:from-primary/90 hover:to-primary/90",
  avatarGradient: "from-primary to-primary/80",
  settingsButton: "hover:bg-primary/10 border-primary/30",
  authButton:
    "from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80",
  authShadow: "hover:shadow-primary/20",
  loading: "border-primary",
  quotaBg: "bg-primary/20 border-primary/50",
  quotaText: "text-primary",
  quotaBar: "bg-primary",
  quotaHover: "text-primary hover:text-primary/80 hover:bg-primary/20",
  headerBorder: "border-primary/20",
  headerText: "text-primary",
  headerHover: "hover:bg-primary/20",
  conversationHover: "hover:bg-primary/10",
  conversationIcon: "bg-primary/20 text-primary",
};

export function getThemeClasses(_theme: ThemeColor) {
  return PRIMARY_THEME_CLASSES;
}
